<script setup lang="ts">
import RlCard from "@/components/ui/RlCard.vue";
import LeafGlyph from "@/components/ui/LeafGlyph.vue";
import RlPill from "@/components/ui/RlPill.vue";

/**
 * The focus bar above Question and Tutor (PRD §6.8 step 3). Purely
 * presentational: the container owns the clock, the goal and the block
 * lifecycle, so this file can never disagree with the state machine.
 *
 * Take-a-break is available at all times, not only at the ~25-minute offer.
 * A break a student has to earn is a forced break wearing a nicer hat
 * (PE-8.5), and the whole point is that it is self-regulated.
 */

const props = defineProps<{
  mode: "question" | "tutor";
  scopeLabel: string;
  /** Precomputed goal progress, e.g. "07:20 / 25:00" or "18 / 40 marks". */
  goalLabel: string;
  /** 0–1 progress toward the goal. */
  fraction: number;
  meta: string;
}>();

defineEmits<{ takeBreak: []; end: [] }>();

/**
 * Clamped here as well as in the selector: a fill wider than its track
 * pushes the leaf marker off the end of the bar, which reads as a bug at
 * exactly the moment the student has done well.
 */
const pct = (): string =>
  `${Math.min(100, Math.max(0, props.fraction * 100))}%`;
</script>

<template>
  <RlCard>
    <div class="bar">
      <div class="bar-main">
        <div class="bar-head">
          <RlPill :tone="mode === 'tutor' ? 'info' : 'accent'">
            {{ mode === "tutor" ? "Tutor" : "Question" }}
          </RlPill>
          <span class="bar-scope">{{ scopeLabel }}</span>
          <span class="bar-goal">{{ goalLabel }}</span>
        </div>

        <div
          class="track"
          role="progressbar"
          :aria-valuenow="Math.round(fraction * 100)"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="goalLabel"
        >
          <div class="fill" :style="{ width: pct() }"></div>
          <LeafGlyph class="marker" :size="14" :style="{ left: pct() }" />
        </div>

        <p class="bar-meta">{{ meta }}</p>
      </div>

      <div class="bar-actions">
        <button type="button" class="button" @click="$emit('takeBreak')">
          Take a break
        </button>
        <button type="button" class="button" @click="$emit('end')">
          End session
        </button>
      </div>
    </div>
  </RlCard>
</template>

<style scoped>
.bar {
  display: flex;
  gap: var(--sp-6);
  align-items: center;
}

.bar-main {
  flex: 1;
  min-width: 0;
}

.bar-head {
  display: flex;
  gap: var(--sp-2);
  align-items: center;
  margin-bottom: var(--sp-2);
}

.bar-scope {
  overflow: hidden;
  font-size: var(--rl-text-sm);
  font-weight: 600;
  color: var(--rl-fg-1);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bar-goal {
  margin-left: auto;
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-sm);
  font-weight: 500;
  color: var(--rl-fg-2);
  white-space: nowrap;
}

.track {
  position: relative;
  height: 6px;
  background: var(--rl-bg-muted);
  border-radius: var(--radius-pill);
}

.fill {
  height: 100%;
  background: var(--rl-green-600);
  border-radius: inherit;
  transition: width var(--t-slow) var(--ease-standard);
}

/* The marker rides the tip of the fill, so it shares the fill's transition. */
.marker {
  position: absolute;
  top: 50%;
  color: var(--rl-green-700);
  transform: translate(-50%, -50%);
  transition: left var(--t-slow) var(--ease-standard);
}

.bar-meta {
  margin: var(--sp-2) 0 0;
  font-size: var(--rl-text-xs);
  color: var(--rl-fg-3);
}

.bar-actions {
  display: flex;
  flex: none;
  gap: var(--sp-2);
}

.bar-actions .button {
  padding: var(--sp-1) var(--sp-3);
  font-size: var(--rl-text-xs);
}
</style>
